import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import GoogleCalendarSyncLogDialog from "./GoogleCalendarSyncLogDialog";
import { googleCalendarErrorMessage, useGoogleCalendarConnect } from "./googleCalendarConnect";

// status.sync_error_code values the backend derives from the org's sync state
// (googleCalendar.syncErrors.*). The raw error text is only shown in the sync log dialog.
const SYNC_ERROR_CODES = ["token_expired", "connection_error", "sync_failed"];

function GoogleCalendarCard({ t }) {
  const { currentOrganization, patchCurrentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  // Status state
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Calendar selection state
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState("");
  const [savingCalendar, setSavingCalendar] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);

  // Disconnect state
  const [disconnecting, setDisconnecting] = useState(false);

  // Sync log dialog
  const [syncLogOpen, setSyncLogOpen] = useState(false);

  // Fetch status on mount and when organization changes
  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `/api/organizations/${organizationId}/google-calendar/status/`
      );
      setStatus(response.data);
      setSelectedCalendar(response.data.calendar_id || "");
      // Keep the store's copy (read by the calendar-view banner) in step with the server.
      patchCurrentOrganization({
        google_calendar_status: response.data.status,
        google_calendar_enabled: response.data.enabled,
      });
    } catch (err) {
      console.error("Failed to fetch Google Calendar status:", err);
      setError(t("googleCalendar.errorFetchingStatus"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t, patchCurrentOrganization]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Fetch calendars when connected but no calendar selected
  const fetchCalendars = useCallback(async () => {
    if (!organizationId || !status?.enabled) return;

    setLoadingCalendars(true);
    try {
      const response = await axios.get(
        `/api/organizations/${organizationId}/google-calendar/calendars/`
      );
      setCalendars(response.data.calendars || []);
    } catch (err) {
      console.error("Failed to fetch calendars:", err);
    } finally {
      setLoadingCalendars(false);
    }
  }, [organizationId, status?.enabled]);

  useEffect(() => {
    if (status?.enabled) {
      fetchCalendars();
    }
  }, [status?.enabled, fetchCalendars]);

  // OAuth popup flow (shared with the calendar-view reconnect banner)
  const {
    connect,
    connecting,
    error: connectError,
  } = useGoogleCalendarConnect({ organizationId, t, onConnected: fetchStatus });

  // Handle calendar selection
  const handleCalendarChange = async (event) => {
    const calendarId = event.target.value;
    setSelectedCalendar(calendarId);

    if (!calendarId) return;

    setSavingCalendar(true);
    try {
      await axios.post(
        `/api/organizations/${organizationId}/google-calendar/select-calendar/`,
        { calendar_id: calendarId }
      );
      await fetchStatus();
    } catch (err) {
      console.error("Failed to select calendar:", err);
      setError(googleCalendarErrorMessage(err, t, "googleCalendar.errorSelectingCalendar"));
    } finally {
      setSavingCalendar(false);
    }
  };

  // Handle manual sync
  const handleSync = async () => {
    if (!organizationId) return;

    setSyncing(true);
    setError("");

    try {
      await axios.post(
        `/api/organizations/${organizationId}/google-calendar/sync/`
      );
      // Wait a moment then refresh status
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      console.error("Failed to start sync:", err);
      setError(googleCalendarErrorMessage(err, t, "googleCalendar.errorSyncing"));
    } finally {
      setSyncing(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!organizationId) return;

    setDisconnecting(true);
    setError("");

    try {
      await axios.post(
        `/api/organizations/${organizationId}/google-calendar/disconnect/`
      );
      await fetchStatus();
      setCalendars([]);
      setSelectedCalendar("");
    } catch (err) {
      console.error("Failed to disconnect:", err);
      setError(googleCalendarErrorMessage(err, t, "googleCalendar.errorDisconnecting"));
    } finally {
      setDisconnecting(false);
    }
  };

  // Render status chip
  const renderStatusChip = () => {
    if (!status) return null;

    const statusConfig = {
      not_connected: { label: t("googleCalendar.notConnected"), color: "default", icon: <LinkOffIcon /> },
      connected: { label: t("googleCalendar.connected"), color: "success", icon: <CheckIcon /> },
      error: { label: t("googleCalendar.error"), color: "error", icon: <ErrorIcon /> },
      token_expired: { label: t("googleCalendar.tokenExpired"), color: "warning", icon: <ErrorIcon /> },
    };

    const config = statusConfig[status.status] || statusConfig.not_connected;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        data-testid="google-calendar-status-chip"
      />
    );
  };

  // Format last sync time
  const formatLastSync = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Check if Google Calendar is available (credentials configured)
  if (!loading && status && !status.google_calendar_available) {
    return null; // Don't show card if not configured
  }

  const tokenExpired = status?.status === "token_expired";
  // A reconnect is needed both when the org was auto-disabled (enabled=false) and when a
  // transient refresh failure left enabled=true with status token_expired.
  const needsConnect = !status?.enabled || tokenExpired;
  // A connection can be stored but broken (expired authorisation, or auto-disabled after a
  // permanent refresh failure). Reconnecting is then not the only sensible option, so the
  // stored connection can also be removed from here.
  const hasStoredConnection = Boolean(
    status?.enabled || status?.account_email || (status?.status && status.status !== "not_connected")
  );
  const shownError = error || connectError;
  const syncErrorCode = SYNC_ERROR_CODES.includes(status?.sync_error_code) ? status.sync_error_code : "";

  return (
    <Card variant="outlined" data-testid="google-calendar-card">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "#4285F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <CalendarIcon />
          </Box>
          <Box sx={{ ml: 2, flex: 1 }}>
            {renderStatusChip()}
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          {t("googleCalendar.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("googleCalendar.description")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {shownError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {shownError}
              </Alert>
            )}

            {syncErrorCode && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="google-calendar-sync-error">
                {t(`googleCalendar.syncErrors.${syncErrorCode}`)}
              </Alert>
            )}

            {needsConnect ? (
              // Not connected (or authorisation expired) - show connect button, plus a way to
              // remove a broken connection instead of only retrying it.
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                  onClick={connect}
                  disabled={connecting || disconnecting}
                  data-testid="google-calendar-connect-button"
                >
                  {connecting
                    ? t("googleCalendar.connecting")
                    : tokenExpired
                      ? t("googleCalendar.reconnect")
                      : t("googleCalendar.connect")}
                </Button>

                {hasStoredConnection && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={disconnecting ? <CircularProgress size={16} /> : <LinkOffIcon />}
                    onClick={handleDisconnect}
                    disabled={disconnecting || connecting}
                    data-testid="google-calendar-remove-button"
                  >
                    {disconnecting ? t("googleCalendar.disconnecting") : t("googleCalendar.disconnect")}
                  </Button>
                )}
              </Box>
            ) : (
              // Connected - show calendar selection and actions
              <Box>
                {/* Connected account info */}
                {status.account_email && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t("googleCalendar.connectedAs")}: <strong>{status.account_email}</strong>
                  </Typography>
                )}

                {/* Calendar selection */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>{t("googleCalendar.selectCalendar")}</InputLabel>
                  <Select
                    value={selectedCalendar}
                    onChange={handleCalendarChange}
                    label={t("googleCalendar.selectCalendar")}
                    disabled={loadingCalendars || savingCalendar}
                  >
                    {loadingCalendars ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {t("Loading...")}
                      </MenuItem>
                    ) : (
                      calendars.map((cal) => (
                        <MenuItem key={cal.id} value={cal.id}>
                          {cal.summary} {cal.primary && t("googleCalendar.primary")}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Last sync info */}
                {status.last_sync_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("googleCalendar.lastSync")}: {formatLastSync(status.last_sync_at)}
                  </Typography>
                )}

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleSync}
                    disabled={syncing || !selectedCalendar}
                  >
                    {syncing ? t("googleCalendar.syncing") : t("googleCalendar.syncNow")}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setSyncLogOpen(true)}
                  >
                    {t("googleCalendar.viewSyncLog")}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={disconnecting ? <CircularProgress size={16} /> : <LinkOffIcon />}
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? t("googleCalendar.disconnecting") : t("googleCalendar.disconnect")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </CardContent>

      {/* Sync Log Dialog */}
      <GoogleCalendarSyncLogDialog
        open={syncLogOpen}
        onClose={() => setSyncLogOpen(false)}
        organizationId={organizationId}
      />
    </Card>
  );
}

export default withTranslation()(GoogleCalendarCard);
